<script setup lang="ts">
import { ExclamationCircleIcon } from "@heroicons/vue/20/solid";
import { twMerge } from "tailwind-merge";
import { computed } from "vue";
import { RouterLink } from "vue-router";

import { useUser } from "@/composables/api/useUser";
import { rewards } from "@/rewards";

import Card from "@/components/ui/Card.vue";
import RewardStepper from "@/components/ui/RewardStepper.vue";
import Spinner from "@/components/ui/Spinner.vue";

const { data: user } = useUser();

const nextReward = computed(() => {
  if (!user.value?.current_mechanic) {
    return;
  }
  const points = user.value ? user.value.loyalty_points : 0;
  const mechanic = user.value.current_mechanic;
  return rewards[mechanic].find((reward) => reward.points > points);
});

const remainingPoints = computed(() => {
  const points = user.value ? user.value.loyalty_points : 0;
  return nextReward.value ? nextReward.value.points - points : 0;
});
</script>

<template>
  <div
    :class="
      twMerge(
        'mx-auto mb-6 flex w-full flex-col items-center gap-9 pt-4 lg:mb-16 lg:flex-row lg:justify-center lg:gap-0 lg:pt-16',
        user?.current_mechanic === 'lactel' && 'bg-[linear-gradient(#B7E2FF_4%,white_44%)] pt-0',
      )
    "
  >
    <Card class="max-w-[342px] lg:max-w-[640px]" />

    <div
      class="flex max-w-2xl flex-col items-center gap-3 lg:-ml-8 lg:gap-8 lg:rounded-[40px] lg:bg-white lg:py-10 lg:pr-8 lg:pl-16 lg:shadow-lg"
    >
      <RouterLink
        :to="{ name: 'how-it-works' }"
        :class="
          twMerge(
            'text-secondary flex items-center gap-1 text-sm underline lg:order-1',
            user?.current_mechanic === 'lactel' && 'text-lactel-grey',
          )
        "
      >
        <ExclamationCircleIcon class="size-5 rotate-180" />
        {{ $t("home.howItWorks") }}
      </RouterLink>

      <template v-if="user">
        <div v-if="nextReward" class="flex flex-col gap-2 lg:gap-4">
          <p
            :class="
              twMerge(
                'mx-auto text-center font-medium text-balance text-black lg:text-2xl [&>span]:font-bold',
                user?.current_mechanic === 'lactel' && 'text-lactel-blue',
              )
            "
            v-html="$t('home.card.remainingPoints', { n: remainingPoints })"
          />

          <p
            :class="
              twMerge(
                'text-galbani font-brygada text-center text-[22px] leading-[130%] font-bold lg:text-[32px]',
                user?.current_mechanic === 'lactel' && 'font-mitr text-lactel-blue',
              )
            "
            v-html="$t(nextReward.name)"
          />
        </div>

        <p
          v-else
          class="mx-auto text-center font-medium text-balance text-black lg:text-2xl [&>span]:font-bold"
          v-html="$t('home.card.allRewardsUnlocked')"
        />

        <RewardStepper :points="user.loyalty_points" />
      </template>

      <template v-else>
        <Spinner class="size-12 lg:size-16" />
      </template>
    </div>
  </div>
</template>
